import default_avatar from '@/assets/images/default-avatar.jpg';
import { Button } from '@/components/Button';
import { CardGrid } from '@/components/CardGrid';
import { Image } from '@/components/Image';
import { SelectInput } from '@/components/SelectInput';
import { PremiumToast } from '@/components/Stripe/PremiumToast';
import { Text } from '@/components/Text';
import { useSubscription } from '@/hooks/useSubscription';
import { SendMeetingRequestModal } from '@/partials/dashboard/Events/EventDetails/SendMeetingRequestModal';
import { useAuthStore } from '@/stores/useAuthStore';
import useContactStore from '@/stores/useContactStore';
import { useEventStore } from '@/stores/useEventStore';
import type { EventUser } from '@/types/event';
import { cn } from '@/utils/cn';
import getDefaultCover from '@/utils/getDefaultCover';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { HiOutlineEye, HiOutlineSearch } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, when: 'beforeChildren' },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

// User Card component to display individual user cards
interface UserCardProps {
  user: EventUser;
  onViewProfile: (user: EventUser) => void;
  onAddContact: (user: EventUser) => void;
  onScheduleMeeting: (user: EventUser) => void;
}

const UserCard = ({
  user,
  onViewProfile,
  onAddContact,
  onScheduleMeeting,
}: UserCardProps) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthStore();
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Get the appropriate cover image based on user card data
  const getCoverImage = () => {
    if (!user.card) {
      return getDefaultCover();
    } else if (user.card.cover) {
      return user.card.cover;
    } else {
      return getDefaultCover(user.card.color);
    }
  };

  return (
    <div
      className={cn(
        'relative flex size-full cursor-pointer flex-col items-center overflow-hidden rounded-2xl bg-white shadow-md',
      )}
      onClick={() => onViewProfile(user)}
    >
      {/* Card header with cover and avatar */}
      <div className="relative mb-12 h-32 w-full">
        {/* Cover image */}
        <Image
          src={getCoverImage()}
          alt="Cover"
          className="size-full object-cover"
        />

        {/* View profile icon */}
        {user.card && (
          <div
            className="absolute right-2 top-2 flex size-8 cursor-pointer items-center justify-center rounded-full bg-white/80 text-gray-700 shadow-md transition-colors hover:bg-white"
            onClick={e => {
              e.stopPropagation();
              onViewProfile(user);
            }}
          >
            <HiOutlineEye className="size-5 text-linko-light" />
          </div>
        )}

        {/* Avatar */}
        {user.card?.avatar ? (
          <Image
            src={user.card.avatar || default_avatar}
            alt="avatar"
            className="absolute bottom-0 left-1/2 size-24 -translate-x-1/2 translate-y-1/2 rounded-full border-4 border-white object-cover"
          />
        ) : (
          <div className="absolute bottom-0 left-1/2 flex size-24 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-gray-200 text-2xl font-bold text-gray-600">
            {user.firstname?.[0]}
            {user.lastname?.[0]}
          </div>
        )}
      </div>
      <div className="flex size-full flex-col items-center justify-between p-4">
        {/* Body */}
        <div className="mt-2 flex flex-col items-center justify-center text-center">
          <h2 className="text-xl font-bold">
            {user.firstname} {user.lastname}
          </h2>
          <p className="text-sm text-gray-500">
            {t(`common:event:user_types.${user.type}`)}
          </p>
          {user.card?.job && (
            <p className="text-sm text-gray-500">{user.card.job}</p>
          )}
          {user.card?.biography && (
            <p className="mt-2 text-sm text-gray-500">{user.card.biography}</p>
          )}
        </div>

        {/* Actions */}
        <div
          className="mt-4 flex w-full flex-col gap-2"
          onClick={stopPropagation}
        >
          <Button
            className="w-full bg-linko-light"
            onClick={e => {
              e.stopPropagation();
              onAddContact(user);
            }}
            disabled={user.id === currentUser?.id}
          >
            {t('eventDetails:addContact')}
          </Button>
          <Button
            className="w-full bg-linko-dark text-white transition-colors duration-300 hover:bg-linko-light"
            onClick={e => {
              e.stopPropagation();
              onScheduleMeeting(user);
            }}
            disabled={user.id === currentUser?.id}
          >
            {t('eventDetails:scheduleMeeting')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EventDetailsUsers = () => {
  const { users, filters, setFilters, fetchEventUsers, loading } =
    useEventStore();
  const { createContact } = useContactStore();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const { hasActiveSubscription } = useSubscription(user ?? null);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const [isMeetingRequestModalOpen, setIsMeetingRequestModalOpen] =
    useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      await fetchEventUsers(Number(id), {
        paginate: 0,
        type: filters.type || 'all',
      });
    };

    fetchUsers();
  }, [id, fetchEventUsers, filters.type]);

  const handleSearch = async (value: string) => {
    setSearchValue(value);
    setFilters({
      current_page: 1,
      search: value,
    });

    await fetchEventUsers(Number(id), {
      paginate: 0,
      search: value,
      type: filters.type || 'all',
    });
  };

  const handleViewProfile = (eventUser: EventUser) => {
    if (eventUser.card) {
      navigate(`/${i18n.language}/profile/${eventUser.card.slug}`);
    } else {
      toast.error(t('eventDetails:noCardFound'));
    }
  };

  const handleAddToContact = async (eventUser: EventUser) => {
    if (!hasActiveSubscription()) {
      toast.custom(toastInstance => (
        <PremiumToast toastInstance={toastInstance} navigate={navigate} />
      ));
      return;
    }
    if (eventUser.id == user?.id) {
      toast.error(t('eventDetails:youCannotAddYourself'));
      return;
    }
    const contact = await createContact(user?.id as number, {
      full_name: eventUser.firstname + ' ' + eventUser.lastname,
      email: eventUser.email,
      phone: eventUser.phone,
      company: eventUser.company?.name ?? '',
      notes: '',
      source: 'event',
      user_id: eventUser.id,
    });

    if (contact) {
      toast.success(t('eventDetails:userAddedToContacts'));
    }
  };

  const handleScheduleMeeting = (eventUser: EventUser) => {
    setIsMeetingRequestModalOpen(true);
    setRecipientId(eventUser.user_id);
  };

  return (
    <>
      <div className="h-full overflow-y-auto md:space-y-6">
        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
          <Text className="mb-2 text-xl font-semibold">
            {t('eventDetails:attendees')} ({filters.total || 0})
          </Text>
          <div className="mb-4 flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SelectInput
              containerStyle="w-full md:w-auto"
              options={[
                { value: 'all', label: t('common:event:user_types.all') },
                { value: 'expert', label: t('common:event:user_types.expert') },
                {
                  value: 'visitor',
                  label: t('common:event:user_types.visitor'),
                },
                {
                  value: 'exhibitor',
                  label: t('common:event:user_types.exhibitor'),
                },
              ]}
              onChange={event => {
                const value = String(event.target.value);
                setFilters({ type: value });
              }}
              name="type"
              defaultValue={filters.type || 'all'}
              useFormik={false}
            />
            <div className="relative w-full md:w-auto">
              <input
                type="text"
                value={searchValue}
                onChange={e => handleSearch(e.target.value)}
                placeholder={t('common:search')}
                className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-linko-light focus:outline-none focus:ring-1 focus:ring-linko-light"
              />
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mt-4"
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-4"
          >
            <CardGrid columns={3}>
              <AnimatePresence>
                {users.map((eventUser, index) => (
                  <motion.div
                    key={eventUser.id || index}
                    variants={itemVariants}
                    layout
                  >
                    <UserCard
                      user={eventUser}
                      onViewProfile={handleViewProfile}
                      onAddContact={handleAddToContact}
                      onScheduleMeeting={() => handleScheduleMeeting(eventUser)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </CardGrid>
            {loading && (
              <div className="flex justify-center py-4">
                <div className="size-8 animate-spin rounded-full border-y-2 border-linko-light"></div>
              </div>
            )}
            {!loading && users.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-lg text-gray-500">
                  {t('eventDetails:noUsers')}
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>
      <SendMeetingRequestModal
        userId={recipientId}
        isOpen={isMeetingRequestModalOpen}
        onClose={() => setIsMeetingRequestModalOpen(false)}
      />
    </>
  );
};
